import moment from 'moment';
import cn from 'classnames';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from '../../effects/useAppSelector';
import { useCloManagerUsers } from '../../effects/data-accessors/useCloManagerUsers';
import { constants, roles, routes } from '../../constants';
import { loanActions } from '../../actions/loan.actions';
import { createFilterActions } from '../../actions/filter.actions';
import { BwicFilterType } from '../../types/filters/FilterState';
import { PageConfigType } from '../../types/page-config/PageConfigType';
import { useAppDispatch } from '../../effects/useAppDispatch';
import { formatUtils, isRequestFailed, isRequestNone, isRequesting, isRequestSuccess, moneyUtils, numericUtils } from '../../utils';
import { user } from '../../user';
import { ShowFor } from '../access/ShowFor';
import { Relative } from '../common/Relative';
import { Preloader } from '../common/Preloader';
import { EmptyPlaceholder } from '../common/EmptyPlaceholder';
import { TextEllipsis } from '../common/TextEllipsis';
import { OnHoverTooltip } from '../common/OnHoverTooltip';
import { Table } from '../bidding/common/table';
import { IColumnDefinition } from '../bidding/common/table/types/ColumnDefinition';
import { ColumnBuilder } from '../bidding/common/table/columns/column-builder/ColumnBuilder';
import { collapseStickRightColumn } from '../bidding/common/table/CollapseStickRightColumn';
import { ResetTableSortingButton } from '../bidding/common/table/ResetTableSortingButton';
import { BwicColumnCustomizer } from '../common/pageConfig/column-customizer/BwicColumnCustomizer';
import { usePageConfig } from '../common/pageConfig/usePageConfig';
import { IssuerSearchInput } from './IssuerSearchInput';
import { LoanMonitorExportButton } from './LoanMonitorExportButton';
import { LoanMonitorFilters } from './LoanMonitorFilters';
import { LoanStatusLabel } from './LoanStatusLabel';
import { LoanMonitorNewLabel } from './LoanMonitorNewLabel';
import { LoanMonitorSidePanel } from './LoanMonitorSidePanel';
import { LoanReviewStatusLabel } from './LoanReviewStatusLabel';
import { AnalystLabel } from './AnalystLabel';
import { LenderCallLink } from './lender-call/LenderCallLink';
import { LoanArrangerBankLink } from './LoanArrangerBankLink';
import { LoanCoArrangerBankLinks } from './LoanCoArrangerBankLinks';
import { GuidanceRange } from './GuidanceRange';
import { LoanPriceRange } from './LoanPriceRange';
import { RatingsCombined } from './RatingsCombined';
import { LoanActions } from './LoanActions';
import { SyndicateContactsLink } from './SyndicateContactsLink';
import { IssuerDescription } from './IssuerDescription';
import { UseOfProceeds } from './UseOfProceeds';
import { useArrangers } from '../../effects/data-accessors/useArrangers';
import { useLoanFieldValues } from '../../effects/data-accessors/useLoanFieldValues';
import { useLoansTotalCount } from '../../effects/data-accessors/useLoansTotalCount';
import { createColumnListBuilder } from '../../utils/ColumnListBuilder';
import { ListBuilder } from '../../utils/ListBuilder';
import { loanMonitorColumnLabels } from '../../constants/loanMonitorColumnLabels';
import { Loan } from '../../types/loan/Loan';
import { ColumnConfigKey } from '../../types/page-config/column-customizer/ColumnConfigKey';
import { dataroomProviderTitles } from '../../types/enums/loan/DataroomProvider';
import { SubscriptionFeature } from '../../types/billing/SubscriptionFeature';
import { ValueBlocker } from '../access/ValueBlocker';
import { ActionBlocker } from '../access/ActionBlocker';
import { BlockedFeatureContent, SubscribeLink } from '../access/BlockedFeatureText';
import { useCollateralManagers } from '../../effects/data-accessors/useCollateralManagers';
import { useDebtInvestors } from '../../effects/data-accessors/useDebtInvestors';
import { IntersectionObserverProvider } from '../common/IntersectionObsereverProvider';

const lockColumnValue = <T,>(column: IColumnDefinition<T>): IColumnDefinition<T> => ({
    ...column,
    renderColumnContent: (item, context) => (
        <ValueBlocker feature={SubscriptionFeature.LoanFullDetails}>
            {column.renderColumnContent(item, context)}
        </ValueBlocker>
    ),
});

const filterActions = createFilterActions(BwicFilterType.LoanMonitor);
const pageConfigType = PageConfigType.LoanMonitor;
const tableKey = 'loanMonitor';

const newBadgeColumn: IColumnDefinition<Loan> = {
    columnKey: 'loan-new-badge',
    renderColumnHeaderContent: () => '',
    renderColumnContent: (loan) => <LoanMonitorNewLabel loan={loan} />,
    className: 'data-list-cell-xxs text-right',
    stickLeft: true,
};

const lastUpdatedColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.LastUpdated,
    configKey: ColumnConfigKey.LastUpdated,
    renderColumnHeaderContent: () => 'Last Updated',
    renderColumnContent: (loan) => {
        const lastUpdatedEst = moment.utc(loan.lastUpdated).tz(constants.estTimezone);

        return (
            <OnHoverTooltip overlay={lastUpdatedEst.format('MMM D, YYYY, h:mm A [EST]')}>
                {lastUpdatedEst.format(constants.dateFormatDoubleDay)}
            </OnHoverTooltip>
        );
    },
    sortingField: ColumnConfigKey.LastUpdated,
    className: 'data-list-cell-sm-01',
    stickLeft: true,
};

const issuerColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.IssuerName,
    configKey: ColumnConfigKey.IssuerName,
    renderColumnHeaderContent: () => 'Issuer',
    renderColumnContent: (loan) => (
        <TextEllipsis>{loan.issuerName}</TextEllipsis>
    ),
    sortingField: ColumnConfigKey.IssuerName,
    className: 'data-list-cell-xl',
    stickLeft: true,
};

const statusColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Status,
    configKey: ColumnConfigKey.Status,
    renderColumnHeaderContent: () => 'Status',
    renderColumnContent: (loan) => <LoanStatusLabel status={loan.status} />,
    sortingField: ColumnConfigKey.Status,
};

const reviewColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Review,
    configKey: ColumnConfigKey.Review,
    renderColumnHeaderContent: () => 'Review',
    renderColumnContent: (loan) => loan.review
        ? <LoanReviewStatusLabel
            review={loan.review}
            issuer={loan.issuerName}
            passingReasons={loan.passingReasons}
            otherPassingReasons={loan.otherPassingReasons}
        />
        : <>{constants.emptyPlaceholder}</>,
    sortingField: ColumnConfigKey.Review,
    className: 'data-list-cell-sm',
};

const analystColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.AnalystUserId,
    configKey: ColumnConfigKey.AnalystUserId,
    renderColumnHeaderContent: () => 'Analyst',
    renderColumnContent: (loan) => (
        <AnalystLabel analystUserId={loan.analystUserId} />
    ),
    sortingField: ColumnConfigKey.AnalystUserId,
    className: 'data-list-cell-md',
};

const lenderCallColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.LenderCallDate,
    configKey: ColumnConfigKey.LenderCallDate,
    renderColumnHeaderContent: () => 'Lender Call',
    renderColumnContent: (loan) => loan.lenderCallDate
        ? <LenderCallLink
            issuer={loan.issuerName}
            lenderCallDate={loan.lenderCallDate}
            liveDomestic={loan.lenderCallLiveDomestic}
            liveInternational={loan.lenderCallLiveInternational}
            livePasscode={loan.lenderCallLivePasscode}
            replayDomestic={loan.lenderCallReplayDomestic}
            replayInternational={loan.lenderCallReplayInternational}
            replayPasscode={loan.lenderCallReplayPasscode}
        />
        : <>{constants.emptyPlaceholder}</>,
    sortingField: ColumnConfigKey.LenderCallDate,
    className: 'data-list-cell-lg-02',
};

const arrangerColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.ArrangerReferenceName,
    configKey: ColumnConfigKey.ArrangerReferenceName,
    renderColumnHeaderContent: () => 'Arranger',
    renderColumnContent: (loan) => (
        loan.arrangerReferenceName
            ? <LoanArrangerBankLink companyReferenceName={loan.arrangerReferenceName} />
            : constants.emptyPlaceholder
    ),
    sortingField: ColumnConfigKey.ArrangerReferenceName,
    className: 'data-list-cell-xs-sm',
};

const syndicateColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Contacts,
    configKey: ColumnConfigKey.Contacts,
    renderColumnHeaderContent: () => 'Syndicate',
    renderColumnContent: (loan) => (
        loan.arrangerReferenceName
            ? <SyndicateContactsLink arrangerReferenceName={loan.arrangerReferenceName} contacts={loan.contacts} />
            : constants.emptyPlaceholder
    ),
    sortingField: ColumnConfigKey.Contacts,
    className: 'data-list-cell-sm-01',
};

const dueColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.DueDate,
    configKey: ColumnConfigKey.DueDate,
    renderColumnHeaderContent: () => 'Due',
    renderColumnContent: (loan) => loan.dueDate
        ? moment.utc(loan.dueDate).format('ddd, MMM D, YYYY, h:mm A')
        : <>{constants.emptyPlaceholder}</>,
    sortingField: ColumnConfigKey.DueDate,
    className: 'data-list-cell-lg-02',
};

const guidanceColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Guidance,
    configKey: ColumnConfigKey.Guidance,
    renderColumnHeaderContent: () => 'Guidance',
    renderColumnContent: (loan) => (
        <GuidanceRange guidanceMin={loan.guidanceMin} guidanceMax={loan.guidanceMax} />
    ),
    sortingField: 'GuidanceMin',
};

const floorColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Floor,
    configKey: ColumnConfigKey.Floor,
    renderColumnHeaderContent: () => 'Floor, %',
    renderColumnContent: (loan) => formatUtils.formatDecimal(loan.floor, 2) || constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.Floor,
    className: 'data-list-cell-xs-sm text-right',
};

const priceColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Price,
    configKey: ColumnConfigKey.Price,
    renderColumnHeaderContent: () => 'Price',
    renderColumnContent: (loan) => (
        <LoanPriceRange priceMin={loan.priceMin} priceMax={loan.priceMax} />
    ),
    className: 'data-list-cell-sm text-right',
};

const sizeColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Size,
    configKey: ColumnConfigKey.Size,
    renderColumnHeaderContent: () => 'Size, MM',
    renderColumnContent: (loan) => numericUtils.isNumber(loan.size) ? moneyUtils.amountToMM(loan.size, false, 3) : constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.Size,
    className: 'data-list-cell-xs-sm text-right',
};

const addOnColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.AddOn,
    configKey: ColumnConfigKey.AddOn,
    renderColumnHeaderContent: () => 'Add-on, MM',
    renderColumnContent: (loan) => numericUtils.isNumber(loan.addOn) ? moneyUtils.amountToMM(loan.addOn, false, 3) : constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.AddOn,
    className: 'data-list-cell-sm-01 text-right',
};

const trancheRatingColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.TrancheRating,
    configKey: ColumnConfigKey.TrancheRating,
    renderColumnHeaderContent: () => 'Tranche Rtg, M/S/F',
    renderColumnContent: (loan) => (
        <RatingsCombined
            moodys={loan.trancheRatingMoodys}
            snp={loan.trancheRatingSnP}
            fitch={loan.trancheRatingFitch}
        />
    ),
    sortingField: ColumnConfigKey.TrancheRating,
    className: 'data-list-cell-tranche-rating',
};

const cfRatingColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.CFRating,
    configKey: ColumnConfigKey.CFRating,
    renderColumnHeaderContent: () => 'CF Rtg, M/S/F',
    renderColumnContent: (loan) => (
        <RatingsCombined
            moodys={loan.cfRatingMoodys}
            snp={loan.cfRatingSnP}
            fitch={loan.cfRatingFitch}
        />
    ),
    sortingField: ColumnConfigKey.CFRating,
    className: 'data-list-cell-sm',
};

const termColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.TermYears,
    configKey: ColumnConfigKey.TermYears,
    renderColumnHeaderContent: () => 'Term, yrs',
    renderColumnContent: (loan) => formatUtils.formatDecimal(loan.termYears, 2) || constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.TermYears,
    className: 'data-list-cell-xs-sm text-right',
};

const maturityColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Maturity,
    configKey: ColumnConfigKey.Maturity,
    renderColumnHeaderContent: () => 'Maturity',
    renderColumnContent: (loan) => loan.maturity
        ? moment(loan.maturity).format(constants.dateFormatDoubleDay)
        : constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.Maturity,
    className: 'data-list-cell-sm-01',
};

const facilityColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Facility,
    configKey: ColumnConfigKey.Facility,
    renderColumnHeaderContent: () => 'Facility',
    renderColumnContent: (loan) => loan.facility
        ? <TextEllipsis>{loan.facility}</TextEllipsis>
        : <>{constants.emptyPlaceholder}</>,
    className: 'data-list-cell-xs',
};

const covLiteColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.CovLite,
    configKey: ColumnConfigKey.CovLite,
    renderColumnHeaderContent: () => 'Cov-Lite',
    renderColumnContent: (loan) => loan.covLite == null
        ? constants.emptyPlaceholder
        : (loan.covLite ? 'Yes' : constants.emptyPlaceholder),
    sortingField: ColumnConfigKey.CovLite,
    className: 'data-list-cell-xs-sm',
};

const industryColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Industry,
    configKey: ColumnConfigKey.Industry,
    renderColumnHeaderContent: () => 'Industry',
    renderColumnContent: (loan) => loan.industry
        ? <TextEllipsis>{loan.industry}</TextEllipsis>
        : <>{constants.emptyPlaceholder}</>,
    sortingField: ColumnConfigKey.Industry,
    className: 'data-list-cell-lg',
};

const issuerDescriptionColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.IssuerDescription,
    configKey: ColumnConfigKey.IssuerDescription,
    renderColumnHeaderContent: () => 'Issuer Desc.',
    renderColumnContent: (loan) => (
        <IssuerDescription issuer={loan.issuerName} issuerDescription={loan.issuerDescription} />
    ),
    className: 'data-list-cell-xs-sm',
};

const useOfProceedsColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.UseOfProceeds,
    configKey: ColumnConfigKey.UseOfProceeds,
    renderColumnHeaderContent: () => 'Use of Proceeds',
    renderColumnContent: loan => (
        <UseOfProceeds
            issuer={loan.issuerName}
            useOfProceeds={loan.useOfProceeds}
            useOfProceedsDescription={loan.useOfProceedsDescription}
        />
    ),
    sortingField: ColumnConfigKey.UseOfProceeds,
    className: 'data-list-cell-lg',
};

const closingDateColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.ClosingDate,
    configKey: ColumnConfigKey.ClosingDate,
    renderColumnHeaderContent: () => 'Closing',
    renderColumnContent: (loan) => loan.closingDate
        ? moment(loan.closingDate).format(constants.dateFormatDoubleDay)
        : constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.ClosingDate,
    className: 'data-list-cell-sm-lg',
};

const orderColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Order,
    configKey: ColumnConfigKey.Order,
    renderColumnHeaderContent: () => 'Order, MM',
    renderColumnContent: (loan) => numericUtils.isNumber(loan.order) ? moneyUtils.amountToMM(loan.order) : constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.Order,
    className: 'data-list-cell-sm-01 text-right',
};

const targetColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Target,
    configKey: ColumnConfigKey.Target,
    renderColumnHeaderContent: () => 'Target, MM',
    renderColumnContent: (loan) => numericUtils.isNumber(loan.target) ? moneyUtils.amountToMM(loan.target) : constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.Target,
    className: 'data-list-cell-sm-01 text-right',
};

const allocationColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Allocation,
    configKey: ColumnConfigKey.Allocation,
    renderColumnHeaderContent: () => 'Allocation, MM',
    renderColumnContent: (loan) => numericUtils.isNumber(loan.allocation) ? moneyUtils.amountToMM(loan.allocation) : constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.Allocation,
    className: 'data-list-cell-sm text-right',
};

const allocationPctColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.AllocationPct,
    configKey: ColumnConfigKey.AllocationPct,
    renderColumnHeaderContent: () => 'Allocation, %',
    renderColumnContent: (loan) => numericUtils.isNumber(loan.allocationPct) ? formatUtils.formatDecimal(loan.allocationPct, 2) : constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.AllocationPct,
    className: 'data-list-cell-sm text-right',
};

const callProtectionColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.CallProtection,
    configKey: ColumnConfigKey.CallProtection,
    renderColumnHeaderContent: () => 'Call Protection',
    renderColumnContent: (loan) => loan.callProtection
        ? <TextEllipsis>{loan.callProtection}</TextEllipsis>
        : <>{constants.emptyPlaceholder}</>,
    className: 'data-list-cell-xl',
};

const coArrangerColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.CoArrangers,
    configKey: ColumnConfigKey.CoArrangers,
    renderColumnHeaderContent: () => 'Co-Arranger',
    renderColumnContent: (loan) => (
        <LoanCoArrangerBankLinks issuer={loan.issuerName} coArrangers={loan.coArrangers} />
    ),
    sortingField: ColumnConfigKey.CoArrangers,
    className: 'data-list-cell-sm-01',
};

const currencyColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.CurrencyCode,
    configKey: ColumnConfigKey.CurrencyCode,
    renderColumnHeaderContent: () => 'Ccy',
    renderColumnContent: (loan) => loan.currencyCode || constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.CurrencyCode,
    className: 'data-list-cell-xxs-02',
};

const refIndexColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.RefIndex,
    configKey: ColumnConfigKey.RefIndex,
    renderColumnHeaderContent: () => 'Ref Index',
    renderColumnContent: (loan) => loan.refIndex || constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.RefIndex,
    className: 'data-list-cell-xs-sm',
};

const marginColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Margin,
    configKey: ColumnConfigKey.Margin,
    renderColumnHeaderContent: () => 'Margin',
    renderColumnContent: (loan) => formatUtils.formatDecimal(loan.margin, 3) || constants.emptyPlaceholder,
    sortingField: ColumnConfigKey.Margin,
    className: 'data-list-cell-xs-sm text-right',
};

const dataroomColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Dataroom,
    configKey: ColumnConfigKey.Dataroom,
    renderColumnHeaderContent: () => 'Dataroom',
    renderColumnContent: (loan) => {
        if (!loan.dataroomProvider) return <>{constants.emptyPlaceholder}</>;
        if (!loan.dataroomLink) return <TextEllipsis>{dataroomProviderTitles[loan.dataroomProvider]}</TextEllipsis>;
        return (
            <a
                href={loan.dataroomLink}
                target="_blank"
                rel="noreferrer"
                className="link-filter text-ellipsis"
                onClick={(e) => e.stopPropagation()}
            >
                {dataroomProviderTitles[loan.dataroomProvider]}
            </a>
        );
    },
    sortingField: "DataroomProvider",
    className: 'data-list-cell-sm',
};

const actionsColumn: IColumnDefinition<Loan> = {
    columnKey: ColumnConfigKey.Actions,
    configKey: ColumnConfigKey.Actions,
    renderColumnHeaderContent: () => '',
    renderColumnContent: loan => <LoanActions loan={loan} />,
    className: 'data-list-cell-sm',
    stickRight: true,
};

export function LoanMonitorView() {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const totalRecordNumber = useAppSelector(s => s.loan.totalRecordNumber);
    const currentPageNumber = useAppSelector(s => s.loan.currentPageNumber);
    const shouldRestoreFiltersQueryString = useAppSelector(s => s.loan.shouldRestoreFiltersQueryString);
    const activeLoanId = useAppSelector(s => s.loan.activeLoanId);
    const pageSize = useAppSelector(s => s.loan.pageSize);
    const loans = useAppSelector(s => s.loan.loans);
    const requestState = useAppSelector(s => s.loan.requestState);
    const nextPageRequestState = useAppSelector(s => s.loan.nextPageRequestState);
    const sortingField = useAppSelector(s => s.loan.sortingField);
    const sortDirection = useAppSelector(s => s.loan.sortDirection);
    const infiniteScrollEnabled = totalRecordNumber > currentPageNumber * pageSize;

    const canManageLoans = user.hasRoles(roles.Administrator, roles.DataEntry, roles.CollateralManager);
    const isCollateralManager = user.hasRoles(roles.CollateralManager);

    const { config, requestState: pageConfigRequestState, defaultConfigRequestState } = usePageConfig(pageConfigType, false, BwicFilterType.LoanMonitor);
    const hiddenColumnKeys: ColumnConfigKey[] = new ListBuilder<ColumnConfigKey>()
        // Hide Actions column from Customize Columns for users without access to it
        .addWhen(() => !canManageLoans, ColumnConfigKey.Actions)
        .result();
    const columnsConfig = config?.columns?.filter(
        c => !hiddenColumnKeys.some(k => k.localeCompare(c.name, undefined, { sensitivity: 'accent' }) === 0)
    );
    const isPageConfigLoadCompleted = isRequestSuccess(pageConfigRequestState, defaultConfigRequestState)
        || isRequestFailed(pageConfigRequestState, defaultConfigRequestState);

    const { requestState: analystsRequestState } = useCloManagerUsers(user.current()?.companyId);
    const { requestState: arrangersRequestState } = useArrangers();
    const { requestState: collateralManagersRequestState } = useCollateralManagers();
    const { requestState: debtInvestorsRequestState } = useDebtInvestors();
    const { requestState: fieldValuesRequestState } = useLoanFieldValues();
    const { totalCount: totalLoansCount, requestState: totalLoansRequestState } = useLoansTotalCount(!user.hasFeatures(SubscriptionFeature.LoanFullDetails));

    const isFilterDataLoaded =
        (isRequestSuccess(analystsRequestState) || isRequestFailed(analystsRequestState)) &&
        (isRequestSuccess(arrangersRequestState) || isRequestFailed(arrangersRequestState)) &&
        (isRequestSuccess(collateralManagersRequestState) || isRequestFailed(collateralManagersRequestState)) &&
        (isRequestSuccess(debtInvestorsRequestState) || isRequestFailed(debtInvestorsRequestState)) &&
        (isRequestSuccess(fieldValuesRequestState) || isRequestFailed(fieldValuesRequestState));

    useEffect(() => {
        if (!isPageConfigLoadCompleted || !isFilterDataLoaded) return;

        if (isRequestNone(requestState)) {
            dispatch(loanActions.init());
        } else if (isRequestSuccess(requestState) && shouldRestoreFiltersQueryString) {
            dispatch(loanActions.restoreFilterQueryString());
        }
    }, [dispatch, isPageConfigLoadCompleted, isFilterDataLoaded, requestState, shouldRestoreFiltersQueryString]);

    useEffect(() => {
        return () => {
            dispatch(loanActions.setShouldRestoreFiltersQueryString(true));
        };
    }, [dispatch]);

    const handleApplyFilter = () => {
        dispatch(loanActions.search());
    };

    const handleNextPage = () => {
        dispatch(loanActions.loadNextPage());
    };

    const handleClearFilters = () => {
        dispatch(loanActions.issuerSearchChange([]));
        dispatch(filterActions.resetFilter());
        dispatch(loanActions.search());
    };

    const handleSort = (field: string, direction: string) => {
        dispatch(loanActions.sortChange(field, direction));
        dispatch(loanActions.search());
    };

    const handleSortingReset = () => {
        dispatch(loanActions.sortChange('', ''));
        dispatch(loanActions.search());
    };

    const isLoading = isRequestNone(requestState, fieldValuesRequestState) || isRequesting(requestState, fieldValuesRequestState);
    const isSearchDisabled = isRequestNone(requestState) || isRequesting(requestState, nextPageRequestState) || isLoading;
    const isEmpty = isRequestSuccess(requestState) && loans.length === 0;

    const columns = isPageConfigLoadCompleted && !isLoading && !isEmpty ? createColumnListBuilder<Loan>(columnsConfig)
        // Keep this sequence in sync with ColumnConfig.GetDefaultLoanMonitor on the backend — it is the
        // fallback order used when the page config fails to load. Otherwise the order comes from config.
        .add(
            newBadgeColumn,
            lastUpdatedColumn,
            issuerColumn,
            statusColumn,
            lockColumnValue(industryColumn),
            lockColumnValue(issuerDescriptionColumn),
            lockColumnValue(useOfProceedsColumn),
            arrangerColumn,
            lockColumnValue(coArrangerColumn),
            lockColumnValue(currencyColumn),
            lockColumnValue(facilityColumn),
            sizeColumn,
            addOnColumn,
            lockColumnValue(termColumn),
            lockColumnValue(maturityColumn),
            lockColumnValue(refIndexColumn),
            lockColumnValue(guidanceColumn),
            lockColumnValue(marginColumn),
            lockColumnValue(floorColumn),
            lockColumnValue(priceColumn),
            lockColumnValue(callProtectionColumn),
            lockColumnValue(trancheRatingColumn),
            lockColumnValue(cfRatingColumn),
            lockColumnValue(covLiteColumn),
            lockColumnValue(lenderCallColumn),
            lockColumnValue(dueColumn),
            lockColumnValue(closingDateColumn),
        )
        .addWhen(() => isCollateralManager,
            reviewColumn,
            analystColumn,
            targetColumn,
            orderColumn,
            allocationColumn,
            allocationPctColumn,
        )
        .add(
            lockColumnValue(syndicateColumn),
            lockColumnValue(dataroomColumn),
        )
        .addWhen(() => canManageLoans, collapseStickRightColumn, actionsColumn)
        .result()
        .map(c => new ColumnBuilder(c)) : [];

    const renderLastRow = () => {
        if (
            user.hasFeatures(SubscriptionFeature.LoanFullDetails) ||
            !isRequestSuccess(totalLoansRequestState) ||
            totalLoansCount <= totalRecordNumber
        ) {
            return null;
        }

        return (
            <BlockedFeatureContent
                inline
                className="invisible-data-placeholder"
                text={
                    <>
                        <SubscribeLink /> to see {totalLoansCount - totalRecordNumber} more loan(s).
                    </>
                }
            />
        );
    };

    return (
        <div className="container loan-monitor">
            <div className="sub-header sub-header-pipeline">
                <div className="sub-header-row type04 flex-row">
                    <h1>Loan Monitor</h1>
                    <Relative>
                        <ActionBlocker feature={SubscriptionFeature.LoanFiltersAndSearch}>
                            {blocked => (
                                <IssuerSearchInput
                                    onSearch={handleApplyFilter}
                                    disabled={blocked || isSearchDisabled}
                                />
                            )}
                        </ActionBlocker>
                    </Relative>
                    <div className="flex-item-right controls">
                        <LoanMonitorExportButton
                            disabled={isEmpty || isLoading || isRequesting(nextPageRequestState)}
                        />
                        <ShowFor roles={[roles.Administrator, roles.DataEntry]}>
                            <button className="btn btn-ghost" onClick={() => history.push(routes.createLoan)} >
                                Add Loan
                            </button>
                        </ShowFor>
                    </div>
                </div>
                <div className="sub-header-row type02 sub-header-filters row-align-filters">
                    <LoanMonitorFilters
                        onApply={handleApplyFilter}
                        disabled={isSearchDisabled}
                    />
                    <div className="margin-l-a flex-row controls">
                        <ResetTableSortingButton
                            tableKey={tableKey}
                            onReset={handleSortingReset}
                        />
                        <BwicColumnCustomizer
                            columnLabels={loanMonitorColumnLabels}
                            excludeColumns={hiddenColumnKeys}
                            tabs={{ 'All': pageConfigType }}
                            disabled={!isPageConfigLoadCompleted || isRequesting(requestState)}
                            defaultTab={pageConfigType}
                            requiredFeature={SubscriptionFeature.LoanColumnCustomizer}
                        />
                    </div>
                </div>
            </div>
            <div className="container-flex container-tabs">
                <div className="loan-monitor-cnt flex-row flex-row-full-size position-relative">
                    <Preloader inProgress={isLoading || !isPageConfigLoadCompleted}>
                        {isEmpty ? (
                            <EmptyPlaceholder onResetClick={handleClearFilters} />
                        ) : (
                            <>
                                <IntersectionObserverProvider
                                    disabled={totalRecordNumber <= 50}
                                    observeTableRows={true}
                                    options={{
                                        rootMargin: "0% 100% 0% 100%" // ignore horizontal scrolling
                                    }}
                                >
                                    <Table
                                        className="data-list data-list-striped"
                                        dataItems={loans}
                                        columns={columns}
                                        sticky={true}
                                        tableKey={tableKey}
                                        createRowCustomKey={(loan: Loan) => loan.id}
                                        createSecurityCustomClassName={(loan: Loan) => cn({ active: loan.id === activeLoanId })}
                                        isRightColumnCollapsible={true}
                                        rightColumnCollapsedDefault={false}
                                        infiniteScrollEnabled={infiniteScrollEnabled}
                                        defaultSortBy={sortingField}
                                        defaultSortByDirection={sortDirection}
                                        isNextPageRequesting={infiniteScrollEnabled}
                                        renderLastRow={renderLastRow}
                                        onNextPageRequest={handleNextPage}
                                        onSort={handleSort}
                                    />
                                </IntersectionObserverProvider>
                                <LoanMonitorSidePanel />
                            </>
                        )}
                    </Preloader>
                </div>
            </div>
        </div>
    );
}
